import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { getInvoice, downloadInvoice } from '../api/invoice.api';

export default function InvoiceModal({
  show,
  onHide,
  invoiceId,
  request,
  currentUser,
}) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchInvoice() {
      if (!invoiceId && !request?.invoice) return;

      // Handle both cases: invoice as ObjectId string or populated object
      const idToFetch =
        invoiceId ||
        (request?.invoice?._id ? request.invoice._id : request?.invoice);
      if (!idToFetch) return;

      try {
        setLoading(true);
        setError(null);
        const data = await getInvoice(idToFetch);
        setInvoice(data);
      } catch (err) {
        setError(err?.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    }

    if (show && (invoiceId || request?.invoice)) {
      fetchInvoice();
    } else {
      setInvoice(null);
      setError(null);
    }
  }, [show, invoiceId, request]);

  const handleDownload = async () => {
    if (!invoice) return;

    try {
      await downloadInvoice(invoice._id);
    } catch (err) {
      alert(err?.message || 'Failed to download invoice');
    }
  };

  const formatDate = date => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateShort = date => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount || 0);
  };

  const getStatusBadgeClass = status => {
    switch (status) {
      case 'paid':
        return 'fw-bold';
      case 'pending':
        return 'fw-bold';
      case 'cancelled':
        return 'fw-bold';
      default:
        return 'fw-bold';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-file-invoice me-2"></i>
          Invoice Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading invoice...</p>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        {invoice && !loading && (
          <div>
            {/* Invoice Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h5 className="fw-bold mb-1">
                  Invoice #{invoice.invoiceNumber}
                </h5>
                <p className="text-muted small mb-0">
                  Date: {formatDate(invoice.created)}
                </p>
              </div>
              <span className={getStatusBadgeClass(invoice.status)}>
                {invoice.status?.toUpperCase()}
              </span>
            </div>

            {/* Parties */}
            <div className="row mb-4">
              <div className="col-md-6">
                <h6 className="fw-bold text-primary mb-3">Guest</h6>
                <p className="mb-1 fw-bold">
                  {invoice.guest?.firstName} {invoice.guest?.lastName}
                </p>
                <p className="text-muted small mb-0">
                  {invoice.guest?.email || 'N/A'}
                </p>
              </div>
              <div className="col-md-6">
                <h6 className="fw-bold text-primary mb-3">Host</h6>
                <p className="mb-1 fw-bold">
                  {invoice.host?.firstName} {invoice.host?.lastName}
                </p>
                <p className="text-muted small mb-0">
                  {invoice.host?.email || 'N/A'}
                </p>
              </div>
            </div>

            {/* Hosting Details */}
            {invoice.request && (
              <div className="mb-4 p-3 ">
                <h6 className="fw-bold text-primary mb-3">Hosting Details</h6>
                <div className="row">
                  <div className="col-12 mb-2">
                    <span
                      className="text-muted small"
                      style={{ paddingLeft: '15px' }}
                    >
                      Request ID:
                    </span>
                    <span className="fw-bold ms-2">
                      {invoice.request._id || request?._id || 'N/A'}
                    </span>
                  </div>
                  <div className="col-12 mb-2">
                    <span
                      className="text-muted small"
                      style={{ paddingLeft: '15px' }}
                    >
                      Check-in:
                    </span>
                    <span className="fw-bold ms-2">
                      {formatDateShort(invoice.request.checkIn)}
                    </span>
                    <span
                      className="text-muted small ms-4"
                      style={{ paddingLeft: '15px' }}
                    >
                      Check-out:
                    </span>
                    <span className="fw-bold ms-2">
                      {formatDateShort(invoice.request.checkOut)}
                    </span>
                  </div>
                  {invoice.request.checkIn && invoice.request.checkOut && (
                    <div className="col-12 mb-2">
                      <span
                        className="text-muted small"
                        style={{ paddingLeft: '15px' }}
                      >
                        Duration:
                      </span>
                      <span className="fw-bold ms-2">
                        {Math.ceil(
                          (new Date(invoice.request.checkOut) -
                            new Date(invoice.request.checkIn)) /
                            (1000 * 60 * 60 * 24),
                        )}{' '}
                        night(s)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Breakdown */}
            <div className="mb-4">
              <h6 className="fw-bold text-primary mb-3">Payment Breakdown</h6>
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Description</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Hosting Amount</td>
                    <td className="text-end">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </td>
                  </tr>
                  {invoice.taxAmount > 0 && (
                    <tr>
                      <td>Tax</td>
                      <td className="text-end">
                        {formatCurrency(invoice.taxAmount, invoice.currency)}
                      </td>
                    </tr>
                  )}
                  {invoice.feeAmount > 0 && (
                    <tr>
                      <td>Service Fee</td>
                      <td className="text-end">
                        {formatCurrency(invoice.feeAmount, invoice.currency)}
                      </td>
                    </tr>
                  )}
                  <tr className="table-active">
                    <td className="fw-bold">Total</td>
                    <td className="text-end fw-bold">
                      {formatCurrency(
                        invoice.netAmount || invoice.amount,
                        invoice.currency,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Description */}
            {invoice.description && (
              <div className="mb-4">
                <h6 className="fw-bold text-primary mb-2">Notes</h6>
                <p className="text-muted">{invoice.description}</p>
              </div>
            )}

            {/* Payment Status */}
            {invoice.status === 'paid' && invoice.paidDate && (
              <div className="alert alert-success">
                <i className="fas fa-check-circle me-2"></i>
                Paid on {formatDate(invoice.paidDate)}
              </div>
            )}

            {/* Payment Link */}
            {invoice.status === 'pending' &&
              invoice.status !== 'paid' &&
              invoice.paymentLink &&
              invoice.guest?._id &&
              currentUser?._id &&
              currentUser._id !== invoice?.host?._id && (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <a
                    href={invoice.paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="alert-link"
                  >
                    Click here to pay via Stripe
                  </a>
                </div>
              )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          Close
        </button>
        {invoice && (
          <button className="btn btn-primary" onClick={handleDownload}>
            <i className="fas fa-download me-2"></i>
            Download PDF
          </button>
        )}
        {invoice?.status !== 'paid' &&
          invoice?.status === 'pending' &&
          invoice?.paymentLink &&
          invoice?.guest?._id &&
          currentUser?._id &&
          currentUser._id !== invoice?.host?._id && (
            <a
              href={invoice.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success"
            >
              <i className="fas fa-credit-card me-2"></i>
              Pay Now
            </a>
          )}
      </Modal.Footer>
    </Modal>
  );
}

InvoiceModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  invoiceId: PropTypes.string,
  request: PropTypes.object,
  currentUser: PropTypes.object,
};
